import React, { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  generateScanAndShareQr,
  getScanAndShareStatus,
  getScanAndShareUserDetails,
} from "../../../../store/slices/abhaRegistrationSlice";
import styles from "./AdharRegistrartion.module.scss";

const getResponseList = (payload) => payload?.result?.list || payload || {};

const getErrorMessage = (response, fallback) => {
  const payload = response?.payload;
  if (typeof payload === "string") return payload;
  return (
    payload?.message ||
    payload?.error ||
    response?.error?.message ||
    fallback
  );
};

const getFirstValue = (source, keys) => {
  for (const key of keys) {
    if (source?.[key]) return source[key];
  }
  return "";
};

const getQrImage = (qrValue) => {
  if (!qrValue) return "";
  if (typeof qrValue !== "string") return "";
  if (qrValue.startsWith("data:image")) {
    return qrValue;
  }
  return "";
};

const ScanAndShare = ({
  clinicId,
  clinicName,
  setModalVisible,
  setShowRegistrationModal,
  onBack,
}) => {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.abhaRegistration);
  const [qrImage, setQrImage] = useState("");
  const [qrPayload, setQrPayload] = useState(null);
  const [requestId, setRequestId] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const generateQr = async () => {
    setErrorMessage("");
    setStatusMessage("");
    setQrImage("");

    const response = await dispatch(
      generateScanAndShareQr({
        clinicId,
        clinicName,
        token,
      })
    );

    if (!response?.payload?.success) {
      setErrorMessage(
        getErrorMessage(
          response,
          "Unable to generate Scan and Share QR. Please try again."
        )
      );
      return;
    }

    const list = getResponseList(response?.payload);
    setQrPayload(list);
    setRequestId(
      getFirstValue(list, [
        "requestId",
        "request_id",
        "txnId",
        "transactionId",
        "token",
      ])
    );

    const qrValue = getFirstValue(list, [
      "qrCodeUrl",
      "qrCode",
      "qrData",
      "qr",
      "data",
      "link",
      "url",
    ]);

    const image = getQrImage(qrValue);
    if (image) {
      setQrImage(image);
      return;
    }

    if (qrValue) {
      setQrImage(await QRCode.toDataURL(qrValue));
      return;
    }

    setErrorMessage("Unable to generate Scan and Share QR. Please try again.");
  };

  useEffect(() => {
    if (clinicId && token) {
      generateQr();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, token]);

  const statusPayload = useMemo(
    () => ({
      clinicId,
      requestId,
      scanToken: qrPayload?.token,
      sessionToken: qrPayload?.token,
      qrToken: qrPayload?.token,
      transactionId: qrPayload?.transactionId || requestId,
      counterId: qrPayload?.counterId,
      txnId: qrPayload?.txnId || qrPayload?.transactionId,
      token,
    }),
    [
      clinicId,
      qrPayload?.counterId,
      qrPayload?.token,
      qrPayload?.transactionId,
      qrPayload?.txnId,
      requestId,
      token,
    ]
  );

  const handleCheckStatus = async () => {
    setErrorMessage("");
    const response = await dispatch(getScanAndShareStatus(statusPayload));
    if (!response?.payload?.success) {
      setErrorMessage(
        getErrorMessage(response, "Unable to check scan status. Please try again.")
      );
      return;
    }

    const list = getResponseList(response?.payload);
    const message =
      getFirstValue(list, ["message", "status", "scanStatus"]) ||
      "Waiting for patient scan.";
    setStatusMessage(message);
  };

  const handleFetchPatient = async () => {
    setErrorMessage("");
    const response = await dispatch(getScanAndShareUserDetails(statusPayload));
    const list = getResponseList(response?.payload);
    const sharedProfile = list?.sharedProfile || list?.profile || list?.patient;

    if (response?.payload?.success && sharedProfile) {
      setShowRegistrationModal(false);
      setModalVisible(true);
      return;
    }

    setErrorMessage(
      getErrorMessage(
        response,
        "Patient details not received yet. Please scan the QR and try again."
      )
    );
  };

  return (
    <div className={styles.scanShareWrapper}>
      <div className={styles.scanShareQrBox}>
        {qrImage ? (
          <img src={qrImage} alt="ABDM Scan and Share QR" />
        ) : (
          <div className={styles.scanSharePlaceholder}>
            {loading ? "Generating QR..." : "QR not available"}
          </div>
        )}
      </div>

      <div className={styles.scanShareContent}>
        <h5>Scan and Share</h5>
        <p>
          Ask the patient to scan this QR from their ABHA app. Once shared,
          fetch the patient profile and continue with registration.
        </p>
        {requestId && (
          <small className="d-block text-muted mb-2">Request ID: {requestId}</small>
        )}
        {statusMessage && <p className="text-success mb-2">{statusMessage}</p>}
        {errorMessage && <p className="text-danger mb-2">{errorMessage}</p>}

        <div className="d-flex flex-wrap gap-2">
          {onBack && (
            <Button variant="outline-dark" onClick={onBack} disabled={loading}>
              Back
            </Button>
          )}
          <Button variant="outline-primary" onClick={generateQr} disabled={loading}>
            Refresh QR
          </Button>
          <Button
            variant="outline-secondary"
            onClick={handleCheckStatus}
            disabled={loading || !requestId}
          >
            Check Status
          </Button>
          <Button variant="primary" onClick={handleFetchPatient} disabled={loading}>
            Continue Registration
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScanAndShare;
